import type { Instrument } from "@trader/shared/MarketData"
import type { AccountId, AccountSummary, Position, Side } from "@trader/shared/Money"
import { BigDecimal } from "effect"
import { toast } from "../../ui/toasts.ts"
import type { LineRequest } from "./chartOrders.ts"
import { formatPrice, formatQty } from "./format.ts"
import { mintClientOrderId, mintCloseOrderId, productChoices, suggestedProduct, ticketInstrument } from "./orderTicket.ts"
import { tradingApi } from "./tradingApi.ts"

// What the Positions tab and the chart's lines do to an account (docs/design/0013).

/** Closes a position with a market order for the opposite side and the whole quantity. */
export const closePosition = async (accountId: AccountId, position: Position): Promise<void> => {
  const long = BigDecimal.isPositive(position.qty)
  const result = await tradingApi.placeOrder(accountId, {
    clientOrderId: mintCloseOrderId(),
    instrumentId: position.instrumentId,
    side: long ? "Sell" : "Buy",
    qty: BigDecimal.abs(position.qty),
    type: { _tag: "Market" },
    product: position.product,
    tif: "Day"
  })
  toast("ok", "Close order placed", `${long ? "Sell" : "Buy"} ${formatQty(BigDecimal.abs(position.qty))} ${position.instrumentId} at market (${result.order.id}).`)
}

/** Sends a moved order, or a position's new or removed stop-loss / take-profit. */
export const sendLineRequest = async (accountId: AccountId, request: LineRequest): Promise<void> => {
  switch (request._tag) {
    case "Amend":
      await tradingApi.amendOrder(accountId, request.orderId, request.prices)
      return
    case "Protect":
      await tradingApi.setProtection(accountId, request.instrumentId, request.body)
      return
  }
}

/**
 * An order placed at once from the chart: Buy / Sell at market, or a right-click limit or stop
 * (TradingView's "Sell Limit 10 @ 29,792.60"), on the followed account; on Carryforward when
 * Cash couldn't do it (a short, a CFD).
 */
export const placeChartOrder = async (
  account: AccountSummary,
  instrument: Instrument,
  choice: { readonly side: Side; readonly type: "Market" | "Limit" | "Stop" },
  qty: BigDecimal.BigDecimal,
  price: BigDecimal.BigDecimal | null,
  heldInCash: BigDecimal.BigDecimal
): Promise<void> => {
  const spec = ticketInstrument(instrument)
  const product = suggestedProduct(productChoices(spec.market, account.clock.timeframe), spec.market, choice.side, heldInCash)
  const type = choice.type === "Market" || price === null
    ? { _tag: "Market" as const }
    : choice.type === "Limit"
    ? { _tag: "Limit" as const, limitPrice: price }
    : { _tag: "Stop" as const, stopPrice: price }
  const result = await tradingApi.placeOrder(account.id, {
    clientOrderId: mintClientOrderId(),
    instrumentId: instrument.id,
    side: choice.side,
    qty,
    type,
    product,
    tif: type._tag === "Market" ? "Day" : "GTC"
  })
  const at = type._tag === "Market" ? "at market" : `@ ${formatPrice(price, spec.pricePrecision, spec.currency)}`
  toast("ok", "Order placed", `${choice.side} ${type._tag === "Market" ? "" : `${type._tag} `}${formatQty(qty)} ${instrument.symbol} ${at} (${result.order.id}).`)
}
